import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";

const initialForm = {
  name: "",
  reportType: "",
};

const SuperAdminFormTypes = () => {
  const token = localStorage.getItem("token");
  const [formTypes, setFormTypes] = useState([]);
  const [reportTypes, setReportTypes] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState("");
  const [editForm, setEditForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState("");

  const authConfig = useMemo(
    () => ({
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }),
    [token]
  );

  const fetchFormTypes = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/form-types`,
        authConfig
      );
      const nextReportTypes = res.data?.reportTypes || [];
      setFormTypes(res.data?.formTypes || []);
      setReportTypes(nextReportTypes);
      setForm((prev) => ({
        ...prev,
        reportType: prev.reportType || nextReportTypes[0]?._id || "",
      }));
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to load form types");
    } finally {
      setLoading(false);
    }
  }, [authConfig]);

  useEffect(() => {
    fetchFormTypes();
  }, [fetchFormTypes]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!form.reportType || !form.name.trim()) {
      toast.error("Report type and form type name are required");
      return;
    }

    try {
      setSaving(true);
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/form-types`,
        form,
        authConfig
      );
      setFormTypes((prev) => [res.data.formType, ...prev]);
      setForm((prev) => ({ ...initialForm, reportType: prev.reportType }));
      toast.success(res.data?.message || "Form type added successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to add form type");
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (formType) => {
    setEditingId(formType._id);
    setEditForm({
      name: formType.name || "",
      reportType: formType.reportType?._id || formType.reportType || "",
    });
  };

  const cancelEdit = () => {
    setEditingId("");
    setEditForm(initialForm);
  };

  const saveEdit = async (id) => {
    if (!editForm.reportType || !editForm.name.trim()) {
      toast.error("Report type and form type name are required");
      return;
    }

    try {
      setSaving(true);
      const res = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/form-types/${id}`,
        editForm,
        authConfig
      );
      setFormTypes((prev) =>
        prev.map((item) => (item._id === id ? res.data.formType : item))
      );
      cancelEdit();
      toast.success(res.data?.message || "Form type updated successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to update form type");
    } finally {
      setSaving(false);
    }
  };

  const deleteFormType = async (formType) => {
    const confirmed = window.confirm(`Delete form type "${formType.name}"?`);

    if (!confirmed) {
      return;
    }

    try {
      setDeletingId(formType._id);
      const res = await axios.delete(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/form-types/${formType._id}`,
        authConfig
      );
      setFormTypes((prev) => prev.filter((item) => item._id !== formType._id));
      toast.success(res.data?.message || "Form type deleted successfully");
    } catch (error) {
      toast.error(error?.response?.data?.message || "Unable to delete form type");
    } finally {
      setDeletingId("");
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3 mb-4">
        <div>
          <h2 className="mb-1">Form Types</h2>
          <p className="text-muted mb-0">
            Add forms under a selected report type, then edit or delete them as needed.
          </p>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Link to="/superadmin-report-types" className="btn btn-outline-secondary">
            Report Types
          </Link>
          <Link to="/superadmin-dashboard" className="btn btn-outline-primary">
            Back To Dashboard
          </Link>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12 col-xl-4">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <h4 className="mb-3">Add Form Type</h4>
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label className="form-label">Report Type</label>
                  <select
                    className="form-select"
                    value={form.reportType}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        reportType: event.target.value,
                      }))
                    }
                    disabled={loading || saving || !reportTypes.length}
                    required
                  >
                    <option value="">Select report type</option>
                    {reportTypes.map((reportType) => (
                      <option key={reportType._id} value={reportType._id}>
                        {reportType.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="mb-3">
                  <label className="form-label">Form Type Name</label>
                  <input
                    className="form-control"
                    value={form.name}
                    onChange={(event) =>
                      setForm((prev) => ({ ...prev, name: event.target.value }))
                    }
                    disabled={saving}
                    placeholder="Enter form type"
                    required
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary w-100"
                  disabled={saving || !reportTypes.length}
                >
                  {saving ? "Saving..." : "Add Form Type"}
                </button>
              </form>
            </div>
          </div>
        </div>

        <div className="col-12 col-xl-8">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                  <h4 className="mb-1">Form Type List</h4>
                  <p className="text-muted mb-0">
                    {loading ? "Loading..." : `${formTypes.length} form types`}
                  </p>
                </div>
              </div>

              <div className="table-responsive">
                <table className="table table-striped table-hover align-middle">
                  <thead>
                    <tr>
                      <th style={{ width: "70px" }}>#</th>
                      <th>Report Type</th>
                      <th>Form Type</th>
                      <th className="text-end" style={{ width: "190px" }}>
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && formTypes.length > 0 ? (
                      formTypes.map((formType, index) => (
                        <tr key={formType._id}>
                          <td>{index + 1}</td>
                          <td>
                            {editingId === formType._id ? (
                              <select
                                className="form-select"
                                value={editForm.reportType}
                                onChange={(event) =>
                                  setEditForm((prev) => ({
                                    ...prev,
                                    reportType: event.target.value,
                                  }))
                                }
                                disabled={saving}
                                required
                              >
                                <option value="">Select report type</option>
                                {reportTypes.map((reportType) => (
                                  <option key={reportType._id} value={reportType._id}>
                                    {reportType.name}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              formType.reportType?.name || "-"
                            )}
                          </td>
                          <td>
                            {editingId === formType._id ? (
                              <input
                                className="form-control"
                                value={editForm.name}
                                onChange={(event) =>
                                  setEditForm((prev) => ({
                                    ...prev,
                                    name: event.target.value,
                                  }))
                                }
                                disabled={saving}
                                autoFocus
                              />
                            ) : (
                              formType.name
                            )}
                          </td>
                          <td className="text-end">
                            {editingId === formType._id ? (
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-success"
                                  onClick={() => saveEdit(formType._id)}
                                  disabled={saving}
                                >
                                  Save
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={cancelEdit}
                                  disabled={saving}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="d-flex justify-content-end gap-2">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-primary"
                                  onClick={() => startEdit(formType)}
                                  disabled={Boolean(deletingId)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => deleteFormType(formType)}
                                  disabled={deletingId === formType._id}
                                >
                                  {deletingId === formType._id ? "Deleting..." : "Delete"}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="4" className="text-center text-muted py-4">
                          {loading ? "Loading form types..." : "No form types available."}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuperAdminFormTypes;
